import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { MapPin, Phone, Mail, Clock, ShieldCheck } from "lucide-react";

const Footer = () => {
  const { t, lang, dict } = useI18n();
  const base = `/${lang}`;
  const legalPaths =
    lang === "mx"
      ? { privacy: "/mx/privacidad", cookies: "/mx/cookies", terms: "/mx/terminos" }
      : { privacy: "/en/privacy", cookies: "/en/cookies", terms: "/en/terms" };

  const navItems = [
    { key: "support", href: `${base}#apoyos` },
    { key: "education", href: `${base}#educacion` },
    { key: "consultation", href: `${base}#consulta` },
    { key: "contact", href: `${base}#contacto` },
  ];

  return (
    <footer className="bg-[#0B2545] text-slate-200 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-10 rounded-md bg-white/10 border border-[#D97706] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white" />
            </span>
            <span className="font-serif text-xl font-bold text-white">Fundelarento</span>
          </div>
          <h4 className="text-white font-semibold mb-2">{t("footer.about_title")}</h4>
          <p className="text-sm text-slate-300 leading-relaxed">{t("footer.about_body")}</p>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-3">{t("footer.contact_title")}</h4>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#D97706]" />
              <span>
                <span className="block text-slate-400 text-xs uppercase tracking-wide">{t("footer.address_label")}</span>
                {t("footer.address")}
              </span>
            </li>
            <li className="flex gap-2">
              <Phone className="w-4 h-4 mt-0.5 shrink-0 text-[#D97706]" />
              <a href="tel:+525553504120" className="hover:text-white" data-testid="footer-phone">
                {t("footer.phone")}
              </a>
            </li>
            <li className="flex gap-2">
              <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#D97706]" />
              <a href="mailto:atencion@fundelarento.com" className="hover:text-white" data-testid="footer-email">
                {t("footer.email")}
              </a>
            </li>
            <li className="flex gap-2">
              <Clock className="w-4 h-4 mt-0.5 shrink-0 text-[#D97706]" />
              <span>{t("footer.hours")}</span>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-3">{t("footer.nav_title")}</h4>
          <ul className="space-y-2 text-sm text-slate-300">
            {navItems.map((it) => (
              <li key={it.key}>
                <a href={it.href} className="hover:text-white" data-testid={`footer-nav-${it.key}`}>
                  {t(`footer.nav.${it.key}`)}
                </a>
              </li>
            ))}
          </ul>

          <h4 className="text-white font-semibold mt-6 mb-3">{t("footer.legal_links_title")}</h4>
          <ul className="space-y-2 text-sm text-slate-300" data-testid="footer-legal-links">
            <li>
              <Link to={legalPaths.privacy} className="hover:text-white" data-testid="footer-link-privacy">
                {t("footer.legal_links.privacy")}
              </Link>
            </li>
            <li>
              <Link to={legalPaths.cookies} className="hover:text-white" data-testid="footer-link-cookies">
                {t("footer.legal_links.cookies")}
              </Link>
            </li>
            <li>
              <Link to={legalPaths.terms} className="hover:text-white" data-testid="footer-link-terms">
                {t("footer.legal_links.terms")}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-3">{t("footer.legal_title")}</h4>
          <ul className="space-y-1.5 text-xs text-slate-300 leading-relaxed">
            {(dict.footer.legal_lines || []).map((line, idx) => (
              <li key={idx}>{line}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <span>{t("footer.rights")}</span>
          <span className="max-w-2xl md:text-right">{t("footer.disclaimer")}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
